import type { ApiNode, ApiOnboardingGraph } from '@onefootprint/request-types/dashboard';
import type { InitialEdgeData } from './parse-graph/parse-graph.types';

/**
 * Computes the `minlen` of every edge into a visible join point so that all branches from the
 * split node reach the join point at the same dagre rank. This is the single source of edge
 * minlens — the backend only identifies which node is the join point for which split node
 * (`joinPointContexts`); it deliberately doesn't compute lengths since they depend on which
 * branches are collapsed in the UI.
 *
 * Distances are longest-path distances over the rendered, visibility-filtered edge list. With
 * nested join points a node is reachable by routes of different lengths and its rank is forced
 * by the deepest one, so shortest-path (BFS) depths would produce minlens that disagree with the
 * actual ranks and the slack would land on arbitrary edges.
 */
export const recalculateJoinPointMinlens = (
  edges: InitialEdgeData[],
  graph: ApiOnboardingGraph,
  visibleNodeIds: Set<string>,
): InitialEdgeData[] => {
  const adjacency = new Map<string, string[]>();
  for (const edge of edges) {
    if (!adjacency.has(edge.source)) adjacency.set(edge.source, []);
    adjacency.get(edge.source)?.push(edge.target);
  }

  const minlenOverrides = new Map<string, number>();

  for (const node of graph.nodes) {
    if (!visibleNodeIds.has(node.id)) continue;

    // A legacy join point may be shared by multiple split nodes — compute the balancing per
    // split node and keep the max per edge.
    for (const splitNodeId of splitNodeIdsFor(node)) {
      if (!visibleNodeIds.has(splitNodeId)) continue;

      const depths = longestDepthsFrom(splitNodeId, node.id, adjacency);

      // Sources without a depth aren't reachable from this split node (e.g. they belong to
      // another split node sharing this join point) — leave them to that split's pass.
      const edgesToJP = edges.filter(e => e.target === node.id && depths.has(e.source));
      if (edgesToJP.length === 0) continue;

      const maxSourceDepth = Math.max(...edgesToJP.map(e => depths.get(e.source)!));

      for (const edge of edgesToJP) {
        const sourceDepth = depths.get(edge.source)!;
        const minlen = Math.max(1, maxSourceDepth - sourceDepth + 1);
        minlenOverrides.set(edge.id, Math.max(minlen, minlenOverrides.get(edge.id) ?? 1));
      }
    }
  }

  if (minlenOverrides.size === 0) return edges;

  return edges.map(edge => {
    const newMinlen = minlenOverrides.get(edge.id);
    if (newMinlen === undefined) return edge;
    return { ...edge, data: { ...edge.data, minlen: newMinlen } };
  });
};

/** The split node(s) a node joins: explicit join points carry `splitNodeId` directly, and legacy
 * implicit join points are regular nodes annotated via `joinPointContexts`. */
const splitNodeIdsFor = (node: ApiNode): string[] => {
  const ids = node.context.joinPointContexts.map(c => c.splitNodeId);
  if (node.kind === 'join_point') ids.push(node.splitNodeId);
  return [...new Set(ids)];
};

/**
 * Longest-path distance (in edges) from `startId` to every reachable node, never traversing
 * through `stopId`. Standard DAG longest path: collect the reachable subgraph, then relax edges
 * in topological (Kahn) order.
 */
const longestDepthsFrom = (startId: string, stopId: string, adjacency: Map<string, string[]>): Map<string, number> => {
  const reachable = new Set<string>([startId]);
  const bfsQueue = [startId];
  while (bfsQueue.length > 0) {
    const current = bfsQueue.shift()!;
    for (const child of adjacency.get(current) ?? []) {
      if (child === stopId || reachable.has(child)) continue;
      reachable.add(child);
      bfsQueue.push(child);
    }
  }

  // In-degrees counting only edges within the reachable subgraph
  const inDegree = new Map<string, number>();
  for (const id of reachable) {
    for (const child of adjacency.get(id) ?? []) {
      if (child === stopId || !reachable.has(child)) continue;
      inDegree.set(child, (inDegree.get(child) ?? 0) + 1);
    }
  }

  const depths = new Map<string, number>();
  depths.set(startId, 0);
  const topoQueue = [startId];

  while (topoQueue.length > 0) {
    const current = topoQueue.shift()!;
    const currentDepth = depths.get(current)!;
    for (const child of adjacency.get(current) ?? []) {
      if (child === stopId || !reachable.has(child)) continue;
      depths.set(child, Math.max(depths.get(child) ?? 0, currentDepth + 1));
      const remaining = inDegree.get(child)! - 1;
      inDegree.set(child, remaining);
      if (remaining === 0) topoQueue.push(child);
    }
  }

  return depths;
};
